import { format } from 'date-fns'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { SubmitHandler } from 'react-hook-form'
import { toast } from 'react-toastify'

import {
  useGetArisIngestionApiLogStatusQuery,
  useStartArisIngestionApiMutation,
  useUpsertArisProcessConfigMutation,
} from '../../app/apiSlice'
import { LogEvent } from './types'
import { Inputs } from './utils'

interface UseArisIngestionTransferResult {
  showTimeRange: boolean
  setShowTimeRange: Dispatch<SetStateAction<boolean>>
  isTransferStarted: boolean
  isPollingComplete: boolean
  isTimeout: boolean
  hasFirstPollingResult: boolean
  logData: { events?: LogEvent[] } | undefined
  logError: unknown
  isFetching: boolean
  isStartingTransfer: boolean
  isSavingConfigurations: boolean
  onStartTransfer: SubmitHandler<Inputs>
  onSaveConfigurations: SubmitHandler<Inputs>
}

const COMPLETED_STATUSES = ['COMPLETED', 'FULL_OPERATION_SUCCESS']
const POLLING_INTERVAL_MS = 5000
const POLLING_TIMEOUT_MS = 20 * 60 * 1000

const useArisIngestionTransfer = (processId: string): UseArisIngestionTransferResult => {
  const [showTimeRange, setShowTimeRange] = useState(false)
  const [isTransferStarted, setIsTransferStarted] = useState(false)
  const [isPollingComplete, setIsPollingComplete] = useState(false)
  const [logId, setLogId] = useState<string | null>(null)
  const [isTimeout, setIsTimeout] = useState(false)
  const [hasFirstPollingResult, setHasFirstPollingResult] = useState(false)

  const [startArisIngestion, { isLoading: isStartingTransfer }] = useStartArisIngestionApiMutation()
  const [upsertArisProcessConfig, { isLoading: isSavingConfigurations }] =
    useUpsertArisProcessConfigMutation()

  const pollingInterval =
    !isTransferStarted || isPollingComplete || isTimeout ? 0 : POLLING_INTERVAL_MS

  useEffect(() => {
    if (!isTransferStarted || isPollingComplete || isTimeout || !logId) {
      return
    }

    const timeoutId = setTimeout(() => {
      setIsTimeout(true)
      setIsPollingComplete(true)
      toast.warning('Polling timeout: The operation exceeded 5 minutes without completion')
    }, POLLING_TIMEOUT_MS)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [isTransferStarted, isPollingComplete, isTimeout, logId])

  const {
    data: logData,
    error: logError,
    isFetching,
  } = useGetArisIngestionApiLogStatusQuery(
    { logId: logId ?? '' },
    {
      pollingInterval,
      skip: !isTransferStarted || !logId,
    },
  )

  useEffect(() => {
    if (logData && !hasFirstPollingResult) {
      setHasFirstPollingResult(true)
    }
  }, [logData, hasFirstPollingResult])

  useEffect(() => {
    const hasCompleted = (logData?.events ?? []).some((event: LogEvent) =>
      COMPLETED_STATUSES.includes(event.status),
    )
    if (hasCompleted) {
      setIsPollingComplete(true)
      toast.success('Data ingestion completed!')
    }
  }, [logData])

  const onStartTransfer: SubmitHandler<Inputs> = async ({
    aris_dataset,
    aris_table,
    time_range_start,
    time_range_end,
    should_trigger_load,
    is_incremental_table,
    is_auto_sync,
  }) => {
    setIsTransferStarted(true)
    setIsPollingComplete(false)
    setIsTimeout(false)
    setHasFirstPollingResult(false)
    setLogId(null)

    try {
      const response = await startArisIngestion({
        process_id: processId,
        aris_dataset,
        aris_table,
        time_range_start: time_range_start
          ? format(time_range_start, "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'")
          : null,
        time_range_end: time_range_end ? format(time_range_end, "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'") : null,
        should_trigger_load,
        is_incremental_table,
        is_auto_sync,
      }).unwrap()

      setLogId(response.log_id)
      toast.success('Direct transfer started successfully')
    } catch (error) {
      console.error('Failed to run ARIS Data Ingestion :', error)
      toast.error('Failed to run ARIS Data Ingestion')
      setIsTransferStarted(false)
      setHasFirstPollingResult(false)
    }
  }

  const onSaveConfigurations: SubmitHandler<Inputs> = async ({
    aris_dataset,
    aris_table,
    time_range_start,
    time_range_end,
    should_trigger_load,
    is_incremental_table,
    is_auto_sync,
  }) => {
    try {
      await upsertArisProcessConfig({
        process_id: processId,
        aris_dataset,
        aris_table,
        time_range_start: time_range_start
          ? format(time_range_start, "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'")
          : null,
        time_range_end: time_range_end ? format(time_range_end, "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'") : null,
        should_trigger_load,
        is_incremental_table,
        is_auto_sync,
      }).unwrap()

      toast.success('Configurations saved successfully')
    } catch (error) {
      console.error('Failed to save ARIS configurations:', error)
      toast.error('Failed to save configurations')
    }
  }

  return {
    showTimeRange,
    setShowTimeRange,
    isTransferStarted,
    isPollingComplete,
    isTimeout,
    hasFirstPollingResult,
    logData,
    logError,
    isFetching,
    isStartingTransfer,
    isSavingConfigurations,
    onStartTransfer,
    onSaveConfigurations,
  }
}

export default useArisIngestionTransfer

